import { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import { RouterProvider } from "react-router";
import { setupNewRelic } from "@/shared/telemetry";
import { setupPageTitleElement } from "@/lib/external-script-compat";

import { useSeonSession } from "@/services/fraud-detection";
import { CosmosProvider } from "@/services/cosmos";
import {
  StatsigProviderWithSession,
  StatsigReplayErrorBoundary,
} from "@/shared/abt";
import { logWarn } from "@/services/logging/logger";
import * as events from "@/events";
import { emitPageLoadTracking } from "@/shared/tracking/analytics";
import {
  getCheckoutSeonCookie,
  setCheckoutSeonCookie,
  getCheckoutToken,
  UNDEFINED_CHECKOUT_ID,
  UNDEFINED_CHECKOUT_SEON,
  UNDEFINED_CHECKOUT_TOKEN,
} from "@/services/cookie";
import { initCheckoutId } from "@/lib/checkout-id";
import { reportReturnedFromAuth } from "@/modules/handshake/lib/returned-from-auth";
import { isHandshakeEntryPath } from "@/lib/boot-shell";

import { router } from "./router";

const resolvedCheckoutId = initCheckoutId();

// Emit CHECKOUT_RETURNED_FROM_AUTH when the shopper lands back from the /aui OTP
// flow (URL carries `from=aui`). Fired here at boot — before the token/session
// gate below — so post-OTP token-handoff failures are still counted.
reportReturnedFromAuth(resolvedCheckoutId);

const initialCheckoutId = resolvedCheckoutId ?? UNDEFINED_CHECKOUT_ID;
if (initialCheckoutId === UNDEFINED_CHECKOUT_ID) {
  logWarn("checkout", "App running without checkout ID");
}
// DON'T fallback to UNDEFINED_CHECKOUT_SEON,
// we would let the `useSeonSession` generate a new one if we didn't receive from cookie.
// This ensures we always have a valid Seon session.
const initialCheckoutSeon = getCheckoutSeonCookie();

// Unauthenticated entry that redirects to Auth login — no checkout token by
// design (see isHandshakeEntryPath).
const isHandshakeEntry = isHandshakeEntryPath();

// Read once at boot. The http client re-reads the token per request, so a
// token that lands later still authenticates; there is no point holding the
// first render for it (prod: the old 2s wait almost never found one).
const initialCheckoutToken = getCheckoutToken();
if (!initialCheckoutToken && !isHandshakeEntry) {
  logWarn("checkout", "App booted without checkout token");
}

function App() {
  const { data: checkoutSeon } = useSeonSession(initialCheckoutSeon);

  if (!initialCheckoutSeon && checkoutSeon) {
    setCheckoutSeonCookie(checkoutSeon);
  }

  useEffect(() => {
    const removeEvents = setupNewRelic();
    const cleanupPageTitle = setupPageTitleElement();

    return () => {
      removeEvents();
      cleanupPageTitle();
    };
  }, []);

  useEffect(() => {
    emitPageLoadTracking();
  }, []);

  // eslint-disable-next-line react-hooks/immutability, @typescript-eslint/no-explicit-any
  (window as any).__CHECKOUT_EVENTS__ = events.ALL_EVENTS;

  return (
    <CosmosProvider
      checkoutId={initialCheckoutId}
      checkoutToken={initialCheckoutToken ?? UNDEFINED_CHECKOUT_TOKEN}
      checkoutSeon={checkoutSeon ?? UNDEFINED_CHECKOUT_SEON}
    >
      <StatsigProviderWithSession>
        <StatsigReplayErrorBoundary>
          <CookiesProvider>
            {/* Boot-shell removal lives in the router's RootLayout so it waits
                for the matched page's chunk, not just for the session fetch. */}
            <RouterProvider router={router} />
          </CookiesProvider>
        </StatsigReplayErrorBoundary>
      </StatsigProviderWithSession>
    </CosmosProvider>
  );
}

export { App };
